import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider } from 'react-admin';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
      maxWidth: 500,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    padding: theme.spacing(2),
  },
  noDataText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  historyContainer: {
    padding: theme.spacing(1, 0),
  },
  historyEntry: {
    marginBottom: theme.spacing(3),
  },
  historyLine: {
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    fontSize: '14px',
  },
}));

interface DateChangeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  jobPhaseId: string;
}

interface HistoryEntry {
  userName: string;
  changeDateTime: string;
  newDate: string;
}

export const DateChangeHistoryModal: React.FC<DateChangeHistoryModalProps> = ({
  open,
  onClose,
  title,
  jobPhaseId,
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && jobPhaseId) {
      fetchHistory();
    }
  }, [open, jobPhaseId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await dataProvider.getList(
        'job-phase-custom-date-start-history-report',
        {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'DESC' },
          filter: { jobPhaseId: jobPhaseId },
        },
      );

      if (response.data && response.data.length > 0) {
        const mappedHistory = response.data.map((item: any) => ({
          userName: item.user ? `${item.user.firstName} ${item.user.lastName}` : 'Unknown User',
          changeDateTime: item.createdAt,
          newDate: item.date,
        }));
        setHistory(mappedHistory);
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error('Error fetching prime date history:', err);
      setError('Failed to load history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp: string) => {
    if (!timestamp) return '';
    // Convert ISO string to local timezone
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // Convert ISO string to local timezone
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box className={classes.noDataContainer}>
          <Typography className={classes.noDataText}>
            Error loading history: {error}
          </Typography>
        </Box>
      );
    }

    if (history.length === 0) {
      return (
        <Box className={classes.noDataContainer}>
          <Typography className={classes.noDataText}>
            No change history available for this date
          </Typography>
        </Box>
      );
    }

    return (
      <Box className={classes.historyContainer}>
        {history.map((entry, index) => (
          <Box key={index} className={classes.historyEntry}>
            <Typography variant="body1" className={classes.historyLine}>
              {entry.userName} - {formatDateTime(entry.changeDateTime)}
            </Typography>
            <Typography variant="body1" className={classes.historyLine}>
              {title.replace(' Change History', '')}: {formatDate(entry.newDate)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          color="primary"
        >
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};
